// フォルダ一括ダウンロード(zip)の実行・状態管理
// tasks/plan_folder_bulk_download.md「UI仕様」節・useExportActions.js の構造に揃える
import { ref, computed } from 'vue';
import _ from 'lodash';

const STATUS_PENDING = 'PENDING';
const STATUS_RUNNING = 'RUNNING';
const STATUS_DONE = 'DONE';
const STATUS_FAILED = 'FAILED';

// 準備中ポーリングの間隔(10秒)と上限回数(30回=5分)
const POLL_INTERVAL_MS = 10000;
const POLL_MAX_COUNT = 30;

// ZIP の作成 (再作成を含む) を受け付けたことを、ツールバーの ZIP のボタン (useSelectionArchive.js) に知らせるイベント
export const FOLDER_ARCHIVE_CREATED_EVENT = 'exa:folder-archive-created';

export const useFolderArchive = () => {
  const archive = ref(null);
  const loading = ref(false);
  const errorMessage = ref('');
  const pollTimeoutReached = ref(false);
  // この画面でボタンを押して実行した直後だけtrueにする(開き直したときに回転アイコンを出さないため)。
  const justExecuted = ref(false);

  let pollTimerId = null;
  let pollCount = 0;
  let pollingFolderId = null;

  const translate = (key, fallback = '') =>
    _.get(window.i18n, key) || fallback || key;

  const isPreparing = computed(() => {
    const status = archive.value?.status;
    return status === STATUS_PENDING || status === STATUS_RUNNING;
  });

  const isFailed = computed(() => archive.value?.status === STATUS_FAILED);
  const isDone = computed(() => archive.value?.status === STATUS_DONE);

  const skippedCount = computed(() => archive.value?.skipped_count || 0);
  const skippedItems = computed(() => archive.value?.skipped_items || []);
  const skippedTruncated = computed(() => Boolean(archive.value?.skipped_truncated));

  const resolveApiErrorMessage = (error, fallbackKey, fallbackText) => {
    const responseMessage = error?.response?.data?.message;
    if (typeof responseMessage === 'string' && responseMessage !== '') {
      return responseMessage;
    }
    return translate(fallbackKey, fallbackText);
  };

  // タイマーの停止のみを行う(justExecutedはリセットしない)。startPolling()の再起動時に使う。
  const clearPollTimer = () => {
    if (pollTimerId !== null) {
      clearInterval(pollTimerId);
      pollTimerId = null;
    }
    pollCount = 0;
    pollingFolderId = null;
  };

  const stopPolling = () => {
    clearPollTimer();
    justExecuted.value = false;
  };

  // 準備中(PENDING/RUNNING)のときだけ10秒間隔で状態を取得し直す。
  // 最大30回(5分)で自動停止し、状態がPENDING/RUNNING以外になった時点でも即停止する。
  const startPolling = (folderId) => {
    if (pollingFolderId === folderId) {
      return;
    }
    clearPollTimer();

    if (!isPreparing.value) {
      return;
    }

    pollingFolderId = folderId;
    pollCount = 0;
    pollTimeoutReached.value = false;

    pollTimerId = setInterval(async () => {
      pollCount += 1;

      try {
        const response = await window.axios.get(`/api/folders/${folderId}/archive`);
        archive.value = response.data;
      } catch (error) {
        // 通信失敗時は直前の状態(準備中)を保ったままポーリングを継続する。
        // fetchLatest/fetchLatestInternal の catch (archive=null) はここでは使わない。
      }

      if (!isPreparing.value) {
        stopPolling();
        return;
      }

      if (pollCount >= POLL_MAX_COUNT) {
        stopPolling();
        pollTimeoutReached.value = true;
      }
    }, POLL_INTERVAL_MS);
  };

  const fetchLatestInternal = async (folderId) => {
    if (!folderId) {
      archive.value = null;
      return;
    }

    try {
      const response = await window.axios.get(`/api/folders/${folderId}/archive`);
      archive.value = response.data;
    } catch (error) {
      // 状態取得の失敗は画面を壊さない(初期状態のボタンのまま)
      archive.value = null;
    }
  };

  const fetchLatest = async (folderId) => {
    justExecuted.value = false;
    stopPolling();
    pollTimeoutReached.value = false;
    await fetchLatestInternal(folderId);

    if (isPreparing.value) {
      startPolling(folderId);
    }
  };

  const execute = async (folderId) => {
    if (!folderId || loading.value) {
      return;
    }

    loading.value = true;
    errorMessage.value = '';

    let estimate = null;
    // 作り直し・初回作成のいずれも同じ見積りAPIを呼び、確認ダイアログに件数・容量を出す。
    try {
      // for=archive: ZIP を作る前の見積もりでは、総容量・HOT 容量の上限超過も返す
      const response = await window.axios.get(`/api/folders/${folderId}/download-estimate`, { params: { for: 'archive' } });
      estimate = response.data;
    } catch (error) {
      // 見積り取得の失敗でZIP作成自体はブロックしない(件数・容量なしの文言で続行)。
      estimate = null;
    }

    if (estimate && estimate.over_limit) {
      errorMessage.value = estimate.message
        || translate(
          'folder.download_size_exceeded',
          '一度にダウンロードできるのは:limitまでです。このフォルダは:sizeあります。サブフォルダごとに分けてダウンロードしてください。'
        );
      loading.value = false;
      return;
    }

    const confirmMessage = isDone.value
      ? (estimate
        ? translate(
            'folder.download_recreate_confirm',
            'このフォルダのZIPを作り直します。対象は:count件・:sizeです。よろしいですか?'
          )
            .replace(':count', String(estimate.file_count))
            .replace(':size', estimate.total_size_text)
        : translate(
            'folder.download_recreate_confirm_no_size',
            'このフォルダのZIPを作り直します。よろしいですか?'
          ))
      : (estimate
        ? translate(
            'folder.download_create_confirm',
            'このフォルダのファイルをまとめたZIPを作成します。対象は:count件・:sizeです。よろしいですか?'
          )
            .replace(':count', String(estimate.file_count))
            .replace(':size', estimate.total_size_text)
        : translate(
            'folder.download_create_confirm_no_size',
            'このフォルダのファイルをまとめたZIPを作成します。よろしいですか?'
          ));
    if (!window.confirm(confirmMessage)) {
      loading.value = false;
      return;
    }

    try {
      const response = await window.axios.post(`/api/folders/${folderId}/archive`);
      archive.value = response.data;
      justExecuted.value = true;
      window.dispatchEvent(new CustomEvent(FOLDER_ARCHIVE_CREATED_EVENT));
      window.alert(
        translate('folder.download_started_notice', 'ZIPの作成を開始しました。完了時にメールでお知らせします。')
      );
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (status === 500 && data && data.status) {
        // submitJob失敗等でServiceがFAILEDのarchiveを返すケース(コントローラ参照)。
        // ボタンを再実行可能な状態に戻すため、返ってきたarchiveで状態を更新する。
        // archive.error_message をZIPの欄が出すため、errorMessage は入れない(同じ文が2行出るのを防ぐ)。
        archive.value = data;
      } else {
        errorMessage.value = resolveApiErrorMessage(
          error,
          'folder.download_failed',
          'フォルダのダウンロード準備に失敗しました。'
        );
      }
    } finally {
      loading.value = false;
    }

    if (isPreparing.value) {
      startPolling(folderId);
    } else {
      stopPolling();
    }
  };

  return {
    archive,
    loading,
    errorMessage,
    isPreparing,
    isFailed,
    isDone,
    skippedCount,
    skippedItems,
    skippedTruncated,
    pollTimeoutReached,
    justExecuted,
    fetchLatest,
    execute,
    stopPolling,
  };
};
